import { GENERIC_BINARY_MIME_TYPE } from "./chat";

// NOTE: A deliberate SUBSET of VALID_UPLOAD_MIME_TYPES in
// server/src/vstr-audio/vstr-audio.constant.ts — the server validates against
// its own copy on presign/confirmUpload, so every MIME a user can pick here
// must also be there (platform variants like `audio/x-m4a` / `video/avi`
// included). The reverse does not hold: the server additionally accepts the
// Ogg/Opus family (`audio/ogg`, `audio/opus`, `audio/x-opus+ogg`), which only
// ever arrives from a WhatsApp / Telegram voice note (NSAI-4172) and which no
// client picker should advertise. Adding entries here to "match" the server
// would expose bot-only formats in the UI.
//
// NOTE: This is the *validator* list, and it stays canonical — one spelling per
// format. It used to double as the picker filter too, and the two jobs are not
// symmetric: that asymmetry is what made four of the ten advertised formats
// unusable (NSAI-4873). Hand `UPLOAD_PICKER_MIME_TYPES` to a picker / dropzone
// instead, and run the picker's answer through `resolveUploadMimeType` first.
//
// NOTE: Platform variants (`audio/x-wav`, `audio/vnd.wave`, …) deliberately do
// NOT belong here — they are folded into a canonical entry by
// `UPLOAD_MIME_ALIASES` below. Adding them to the list instead looks equivalent
// and is not: the server's Slack path keys off "not in the allow-list" to decide
// when to derive the MIME from the file extension
// (`resolveTranscribeMimeType`), so listing a variant makes it short-circuit and
// flow through unnormalised, past the one funnel that guarantees validation,
// video classification and extension resolution all see the same spelling.
export const VALID_UPLOAD_MIME_TYPES = [
  "audio/mp3",
  "audio/mpeg",
  "audio/m4a",
  "audio/mp4",
  "audio/x-m4a",
  "audio/wav",
  "audio/wave",
  "audio/webm",
  "video/webm",
  "video/mp4",
  "video/x-matroska",
  "video/quicktime",
  "video/x-msvideo",
  "video/avi",
  "video/msvideo",
] as const;

// NOTE: Canonical extension list rendered in the upload dropzone
// placeholder ("MP3, M4A, WAV, ..."). Audio first, then video; one
// entry per format (no browser variants — those are MIME-only).
export const SUPPORTED_UPLOAD_EXTENSIONS = [
  "mp3",
  "mpeg",
  "m4a",
  "wav",
  "wave",
  "webm",
  "mp4",
  "mkv",
  "mov",
  "avi",
] as const;

// NOTE: What to hand a file picker / dropzone, deliberately *wider* than the
// validator above. A picker filter and a validator cannot be the same list:
// the filter decides what the OS lets the user reach, and both platforms map an
// extension to a MIME type through their own database, so a format with no
// entry there is unreachable no matter how many spellings the allow-list
// carries. `.wave` is the clearest case on Android — MimeTypeMap has no entry
// for it at all, so the provider answers `application/octet-stream` ("BIN file"
// in the picker) and the only filter that reaches it is one admitting unknown
// binaries (NSAI-4873). `.mkv` is the iOS case, and it arrives there by a
// different route: iOS hands it a *dynamic* UTI whose `preferredMIMEType` is nil
// (the generic type is supplied a layer up, by expo-document-picker), and that
// UTI conforms to neither `public.audio` nor `public.movie`. The generic entry
// below is therefore the only reason `.mkv` is selectable at all on iOS —
// measured on iOS 26.1, not read off macOS's UTType database. Do not "tidy" this
// list down to the two wildcards; that silently un-picks `.mkv` again.
//
// The wildcards keep every advertised audio/video format reachable on both
// platforms without enumerating each one, and the generic entry catches what
// neither database recognises. Widening here is safe because it is not the
// gate: `resolveUploadMimeType` canonicalises the picked value and
// `validateUploadFile` still refuses anything outside
// `VALID_UPLOAD_MIME_TYPES`, with a worded reason instead of a silently
// greyed-out row.
export const UPLOAD_PICKER_MIME_TYPES = [
  "audio/*",
  "video/*",
  GENERIC_BINARY_MIME_TYPE,
] as const;

// NOTE: Picker-reported MIME → the canonical spelling the allow-list carries.
// Same job as `PROBE_FILE_MIME_ALIASES` / `KNOWLEDGE_MIME_ALIASES` (NSAI-5017),
// for the audio/video uploader, and the same strategy the server's Slack path
// already used for exactly these strings (`resolveTranscribeMimeType`).
//
// Every key was measured on a device rather than guessed (NSAI-4873): Android's
// MimeTypeMap answers `audio/x-wav` for `.wav` and `video/mpeg` for `.mpeg`;
// iOS's UTType answers `audio/vnd.wave` for `.wav`, the IANA registration and
// the first tag on `com.microsoft.waveform-audio`, so it wins
// `preferredMIMEType`. `audio/x-wave` is the legacy desktop-browser variant.
//
// NOTE: iOS reports `.wave` as `audio/vnd.wave` too — it shares
// `com.microsoft.waveform-audio` with `.wav` — so on iOS this map alone rescues
// `.wave`. The `wave` row in `UPLOAD_EXTENSION_MIME_MAP` is Android-only
// insurance, where the extension is the sole remaining evidence.
//
// NOTE: `.mpeg` collapses to `audio/mpeg` because that is the stance the repo
// already takes — `EXTENSION_TO_MIME_TYPE` maps `mpeg` → `audio/mpeg` and the
// bot display lists collapse "mpeg" into MP3. Both pickers disagree and call it
// video, so a `.mpeg` program stream is transcribed through the audio branch
// (ffmpeg's `-vn` still extracts the track). Reclassifying `.mpeg` as video
// end-to-end is the separate open question the ticket flagged; it is not decided
// here, and the advertised list and the allow-list agree either way.
export const UPLOAD_MIME_ALIASES = {
  "audio/vnd.wave": "audio/wav",
  "audio/x-wav": "audio/wav",
  "audio/x-wave": "audio/wav",
  "video/mpeg": "audio/mpeg",
} as const satisfies Record<string, (typeof VALID_UPLOAD_MIME_TYPES)[number]>;

// NOTE: Extension → MIME fallback for the cases above, where the picker hands
// over `application/octet-stream` or nothing usable. This is the half that
// actually rescues `.wave` on Android and `.mkv` on iOS. The `satisfies` clause
// is the anti-drift guard: the build fails if an advertised extension loses its
// mapping, or if a mapping points at a MIME the validator would reject.
export const UPLOAD_EXTENSION_MIME_MAP = {
  mp3: "audio/mp3",
  mpeg: "audio/mpeg",
  m4a: "audio/m4a",
  wav: "audio/wav",
  wave: "audio/wav",
  webm: "video/webm",
  mp4: "video/mp4",
  mkv: "video/x-matroska",
  mov: "video/quicktime",
  avi: "video/avi",
} as const satisfies Record<
  (typeof SUPPORTED_UPLOAD_EXTENSIONS)[number],
  (typeof VALID_UPLOAD_MIME_TYPES)[number]
>;

export const MAX_AUDIO_UPLOAD_SIZE_BYTES = 250 * 1024 * 1024;
export const MAX_VIDEO_UPLOAD_SIZE_BYTES = 1.5 * 1024 * 1024 * 1024;

// NOTE: NSAI-5143 — ceiling on hashing an upload for the pre-upload duplicate
// guard. The server's own duplicate guard is authoritative and runs on
// confirmUpload, once the object is already in S3; the presign call accepts a
// client-computed hash so the same rejection can happen before a single byte
// moves. `crypto.subtle` has no streaming digest, so the hash costs a transient
// buffer the size of the file — hence a ceiling rather than "always hash".
//
// 256 MB clears MAX_AUDIO_UPLOAD_SIZE_BYTES, so every audio upload is covered
// by construction, plus short videos. Larger videos skip the pre-check and keep
// today's behavior: they are caught by the post-upload guard. That is the known
// gap, not an oversight — closing it needs an incremental digest Web Crypto
// does not offer, and hashing 1.5 GB in JS would tax every non-duplicate upload
// to spare the rare duplicate one.
export const MAX_UPLOAD_CONTENT_HASH_SIZE_BYTES = 256 * 1024 * 1024;

// NOTE: Binary (IEC) steps. These are NOT display divisors — dividing by 1024
// and printing "KB" is exactly the NSAI-4790 defect. They exist because the
// upload caps above are themselves defined in binary (250 * 1024 * 1024, ...)
// and the server enforces those same binary values
// (apps/nsai/server/src/vstr-audio/vstr-audio.constant.ts), so a cap only reads
// back as the round number the copy promises when it is divided the way it was
// built. Also used for the per-megabyte download-progress throttle, where the
// granularity is arbitrary. For the size of a real file, use the decimal
// constants below.
export const BYTES_IN_KILOBYTE = 1024;
export const BYTES_IN_MEGABYTE = BYTES_IN_KILOBYTE * 1024;
export const BYTES_IN_GIGABYTE = BYTES_IN_MEGABYTE * 1024;

// NOTE: NSAI-4790 — decimal (SI) steps, the divisors for displaying the size of
// an actual file. Same decision NSAI-4566 already took for KB_THRESHOLD /
// MB_THRESHOLD in constants/chat.ts, applied to the upload formatter: the
// labels we print are "KB"/"MB"/"GB", and iOS, macOS Finder and Android all
// report file sizes decimally, so a 39,724-byte file must read "39.7 KB" here
// and not the "38.8 KB" a 1024 divisor produced while the OS said "40 KB".
export const BYTES_IN_DECIMAL_KILOBYTE = 1000;
export const BYTES_IN_DECIMAL_MEGABYTE = BYTES_IN_DECIMAL_KILOBYTE * 1000;
export const BYTES_IN_DECIMAL_GIGABYTE = BYTES_IN_DECIMAL_MEGABYTE * 1000;

export const UPLOADED_MEETINGS_ITEMS_PER_PAGE = 10 as const;

// NOTE: If no XHR progress event fires within this window the upload is considered stalled
// (proxy dropped the connection without sending an error). Must stay well under the proxy
// timeout on /s3-proxy (currently 3600 s) so the client, not the proxy, names the failure.
//
// NOTE: NSAI-6451 — this was 60 s, which is not long enough to mean "dead" on a loaded
// deployment. A progress event only fires when storage accepts more bytes, so the window
// measures how long the far end may stop draining the body — and on a single-VM stack,
// where MinIO shares one disk with Postgres, Qdrant and the rest, it demonstrably stops
// for longer than a minute under load. The guard then failed every Transcribe upload that
// ran past the first quiet patch, whatever the file size, and a single PUT has no resume:
// one abort throws the whole transfer away. 180 s still fails a genuinely dead upload
// inside a wait a user will sit through, and it is the same order as the 300 s Caddy
// allows the API. It does NOT make the guard correct — a long-lived transfer is only
// really protected once the upload is chunked (see the rejected agent note
// 2026-09-15-ios-upload-stall-guard-via-transport-timeout); it stops a survivable stall
// from being a permanent failure.
export const UPLOAD_STALL_TIMEOUT_MS = 180_000;

// NOTE: NSAI-4883 — the user-facing sentence names the window in seconds, so derive
// it rather than writing the number into three locale files that cannot be kept in
// step with the constant above.
export const UPLOAD_STALL_TIMEOUT_SECONDS = Math.round(
  UPLOAD_STALL_TIMEOUT_MS / 1000,
);

// NOTE: NSAI-6953 — the request header that carries a declared SHA-256 on a
// presigned storage PUT, as standard padded base64 of the 32 digest bytes. It
// has to be a header. Left to itself, the presigner puts the checksum in the
// URL's query string, which MinIO ignores; only the header makes storage check
// the body and keep the digest. When a checksum was declared at presign, the
// server signs this header into the URL, so a PUT that leaves it out or sends
// another value is refused.
export const S3_CHECKSUM_SHA256_HEADER = "x-amz-checksum-sha256";

// NOTE: NSAI-5132 — ceiling for the provider→client download percentage while
// bytes are still arriving. 100 is reserved for "all bytes in, now decoding
// locally", so the row can say "preparing" instead of parking at 100% for the
// seconds a large video's base64 read takes.
export const MAX_IN_FLIGHT_DOWNLOAD_PROGRESS = 99;

// NOTE: NSAI-4874 — same idea as the download ceiling above: only the XHR
// `load` event may write 100%. React Native's Android uploader double-counts
// the bytes it sends, so `loaded` reaches ~2x the file and the row showed
// "183%". Measured on API 35: 312,875,110 reported against a real 157,286,400.
export const MAX_IN_FLIGHT_UPLOAD_PROGRESS = 99;

// NOTE: NSAI-5144 — date-fns pattern for the exact processing-completion
// moment behind the Uploads badge (tooltip on web, toast on mobile). Extends
// the row's existing "EEEE, d MMM, yyyy" date with the time, because a badge
// reading "about 3 hours ago" is only useful if the precise value is one
// interaction away. Shared so both clients spell the moment the same way.
export const UPLOADS_ABSOLUTE_DATE_TIME_FORMAT = "EEEE, d MMM, yyyy, HH:mm";
